import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@tanium/react-box';
import { FormRow } from '@tanium/react-form';
import { RadioWithLabel } from '@tanium/react-radio';
import { Notification } from '@tanium/react-persistent-notifications';
import { Shimmer } from '@tanium/react-shimmer';
import { Stack } from '@tanium/react-stack';
import { CopyableText } from '../CopyableText';
import { MediumHighlightedText, MediumSubtleText } from '../../TextStyles';
import { DownloadLink } from '../DownloadLink';
import { AsyncStateData } from '../../hooks/useAsync';
import { AsyncSuspense } from '../AsyncSuspense';
import { Api } from '../../api';
import { SamlProviderConfig } from '../../models/IdpModels';
import { parseApiErrorMsg } from '../../hooks/useApiErrorMsgParser';

interface AutoServiceProviderSettingsProps {
  idpName: string;
}

interface ManualServiceProviderSettingsProps {
  samlConfig: SamlProviderConfig | undefined;
}

enum SetupType {
  Auto = 'auto',
  Manual = 'manual',
}

const AutoServiceProviderSettings: React.VFC<AutoServiceProviderSettingsProps> = ({ idpName }) => {
  const { t } = useTranslation();
  return (
    <Box mt={2}>
      <Stack direction="column">
        <MediumSubtleText>{t('Service Provider Metadata Download')}</MediumSubtleText>
        <DownloadLink
          href={`${Api.downloadLink.serviceProviderMetadataXmlPath}?idpName=${idpName}`}
          name="service-provider-metadata.xml"
        >
          {t('Download Service Provider Metadata XML file')}
        </DownloadLink>
      </Stack>
    </Box>
  );
};

const ManualServiceProviderSettings: React.VFC<ManualServiceProviderSettingsProps> = ({ samlConfig }) => {
  const { t } = useTranslation();
  if (!samlConfig) {
    return <></>;
  }

  return (
    <Box>
      <Box mt={1}>
        <MediumSubtleText>{t('SSO URL')}</MediumSubtleText>
        <br></br>
        <MediumHighlightedText>
          <CopyableText text={samlConfig.url} />
        </MediumHighlightedText>
      </Box>
      <Box mt={1}>
        <MediumSubtleText>{t('Audience URI/Entity ID')}</MediumSubtleText>
        <br></br>
        <MediumHighlightedText>
          <CopyableText text={samlConfig.entityID} />
        </MediumHighlightedText>
      </Box>
      <Box mt={1}>
        <MediumSubtleText>{t('Account Management Portal URL')}</MediumSubtleText>
        <br></br>
        <MediumHighlightedText>
          <CopyableText text={window.location.origin} />
        </MediumHighlightedText>
      </Box>
      <Box mt={1}>
        <MediumSubtleText>{t('Logout URL')}</MediumSubtleText>
        <br></br>
        <MediumHighlightedText>
          <CopyableText text={samlConfig.logoutURL} />
        </MediumHighlightedText>
      </Box>
      <Box mt={1}>
        <Stack direction="column">
          <MediumSubtleText>{t('Service Provider Signing Certificate Download')}</MediumSubtleText>
          <DownloadLink href={Api.downloadLink.serviceProviderCertPath} name="service-provider-signing-cert.crt">
            {t('Download Certificate')}
          </DownloadLink>
        </Stack>
      </Box>
    </Box>
  );
};

interface ServiceProviderSettingsInformationProps {
  onChangeSetupType: (type: string) => void;
  setupType: string;
  samlConfig: AsyncStateData<SamlProviderConfig>;
  idpName: string;
}

export const ServiceProviderSettingsInformation: React.VFC<ServiceProviderSettingsInformationProps> = ({
  onChangeSetupType,
  setupType,
  samlConfig,
  idpName,
}) => {
  const { t } = useTranslation();

  const onSelectSetupType: React.ChangeEventHandler<HTMLInputElement> = useCallback(
    ({ currentTarget: { value } }) => {
      onChangeSetupType(value);
    },
    [onChangeSetupType]
  );

  const manualSettingsElement = useMemo(
    () => (
      <Box mt={2}>
        <AsyncSuspense state={samlConfig} loading={<Shimmer />} failure={parseApiErrorMsg(t, samlConfig.error)}>
          <ManualServiceProviderSettings samlConfig={samlConfig.value}></ManualServiceProviderSettings>
        </AsyncSuspense>
      </Box>
    ),
    [samlConfig, t]
  );

  return (
    <Box>
      <FormRow label={t('Method')}>
        <Stack direction="row" itemSpacing={4}>
          <RadioWithLabel
            name="idp-setup-method"
            id="idp-setup-auto"
            label={t('Auto Setup')}
            value={SetupType.Auto}
            checked={setupType === SetupType.Auto}
            onChange={onSelectSetupType}
          ></RadioWithLabel>
          <RadioWithLabel
            name="idp-setup-method"
            id="idp-setup-manual"
            label={t('Manual Setup')}
            value={SetupType.Manual}
            checked={setupType === SetupType.Manual}
            onChange={onSelectSetupType}
          ></RadioWithLabel>
        </Stack>
      </FormRow>
      {setupType === SetupType.Auto && <AutoServiceProviderSettings idpName={idpName} />}
      {setupType === SetupType.Manual && (
        <>
          {!idpName ? (
            <Notification role="status" type="warning">
              {t('Identity Provider Name is required')}
            </Notification>
          ) : (
            manualSettingsElement
          )}
        </>
      )}
    </Box>
  );
};
